/**
 * Detection Plan - Configure Findings Lifecycle Dispositions
 * Allows admins to manage dispositions, wait-until timeframes, and suppression reasons
 */
import React, { useState, useEffect } from 'react';
import { Tabs, Spin, Alert, Button } from 'antd';
import { SettingOutlined, ClockCircleOutlined, StopOutlined, ControlOutlined } from '@ant-design/icons';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import DispositionConfigPanel from './config/DispositionConfigPanel';
import { showError, showSuccess } from '../../../utils/notifications';
import {
  getDispositions,
  createDisposition,
  updateDisposition,
  deleteDisposition,
  reorderDispositions,
  getWaitUntilTimeframes,
  createTimeframe,
  updateTimeframe,
  deleteTimeframe,
  reorderTimeframes,
  getSuppressionReasons,
  createSuppressionReason,
  updateSuppressionReason,
  deleteSuppressionReason,
  reorderSuppressionReasons
} from '../api/trustgridApi';

const { TabPane } = Tabs;

function DetectionPlan() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dispositions');

  // Dispositions state
  const [dispositionsData, setDispositionsData] = useState({
    in_progress: [],
    closed: [],
    config: {}
  });

  // Timeframes state
  const [timeframes, setTimeframes] = useState([]);

  // Suppression reasons state
  const [suppressionReasons, setSuppressionReasons] = useState([]);

  const breadcrumbs = [
    { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    { label: 'Findings', path: '/trustgrid/findings' },
    { label: 'Detection Plan', path: '/trustgrid/findings/detection-plan' }
  ];

  useEffect(() => {
    if (activeTab === 'dispositions') {
      fetchDispositions();
    } else if (activeTab === 'timeframes') {
      fetchTimeframes();
    } else if (activeTab === 'suppression') {
      fetchSuppressionReasons();
    }
  }, [activeTab]);

  // ============================================================
  // Dispositions API Calls
  // ============================================================

  const fetchDispositions = async () => {
    setLoading(true);
    try {
      const res = await getDispositions();
      setDispositionsData(res.data);
    } catch (error) {
      console.error('Failed to fetch dispositions:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDisposition = async (disposition) => {
    try {
      await createDisposition(disposition);
      showSuccess('Disposition created');
      fetchDispositions();
      return true;
    } catch (error) {
      console.error('Failed to create disposition:', error);
      showError(error);
      return false;
    }
  };

  const handleUpdateDisposition = async (id, updates) => {
    try {
      await updateDisposition(id, updates);
      showSuccess('Disposition updated');
      fetchDispositions();
      return true;
    } catch (error) {
      console.error('Failed to update disposition:', error);
      showError(error);
      return false;
    }
  };

  const handleDeleteDisposition = async (id) => {
    try {
      await deleteDisposition(id);
      showSuccess('Disposition deleted');
      fetchDispositions();
      return true;
    } catch (error) {
      console.error('Failed to delete disposition:', error);
      showError(error);
      return false;
    }
  };

  const handleReorderDispositions = async (status, orderedIds) => {
    try {
      await reorderDispositions(status, orderedIds);
      showSuccess('Dispositions reordered');
      fetchDispositions();
      return true;
    } catch (error) {
      console.error('Failed to reorder dispositions:', error);
      showError(error);
      return false;
    }
  };

  // ============================================================
  // Timeframes API Calls
  // ============================================================

  const fetchTimeframes = async () => {
    setLoading(true);
    try {
      const res = await getWaitUntilTimeframes();
      setTimeframes(res.data.timeframes || []);
    } catch (error) {
      console.error('Failed to fetch timeframes:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTimeframe = async (timeframe) => {
    try {
      await createTimeframe(timeframe);
      showSuccess('Timeframe created');
      fetchTimeframes();
      return true;
    } catch (error) {
      console.error('Failed to create timeframe:', error);
      showError(error);
      return false;
    }
  };

  const handleUpdateTimeframe = async (id, updates) => {
    try {
      await updateTimeframe(id, updates);
      showSuccess('Timeframe updated');
      fetchTimeframes();
      return true;
    } catch (error) {
      console.error('Failed to update timeframe:', error);
      showError(error);
      return false;
    }
  };

  const handleDeleteTimeframe = async (id) => {
    try {
      await deleteTimeframe(id);
      showSuccess('Timeframe deleted');
      fetchTimeframes();
      return true;
    } catch (error) {
      console.error('Failed to delete timeframe:', error);
      showError(error);
      return false;
    }
  };

  const handleReorderTimeframes = async (orderedIds) => {
    try {
      await reorderTimeframes(orderedIds);
      showSuccess('Timeframes reordered');
      fetchTimeframes();
      return true;
    } catch (error) {
      console.error('Failed to reorder timeframes:', error);
      showError(error);
      return false;
    }
  };

  // ============================================================
  // Suppression Reasons API Calls
  // ============================================================

  const fetchSuppressionReasons = async () => {
    setLoading(true);
    try {
      const res = await getSuppressionReasons();
      setSuppressionReasons(res.data.reasons || []);
    } catch (error) {
      console.error('Failed to fetch suppression reasons:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuppressionReason = async (reason) => {
    try {
      await createSuppressionReason(reason);
      showSuccess('Suppression reason created');
      fetchSuppressionReasons();
      return true;
    } catch (error) {
      console.error('Failed to create suppression reason:', error);
      showError(error);
      return false;
    }
  };

  const handleUpdateSuppressionReason = async (id, updates) => {
    try {
      await updateSuppressionReason(id, updates);
      showSuccess('Suppression reason updated');
      fetchSuppressionReasons();
      return true;
    } catch (error) {
      console.error('Failed to update suppression reason:', error);
      showError(error);
      return false;
    }
  };

  const handleDeleteSuppressionReason = async (id) => {
    try {
      await deleteSuppressionReason(id);
      showSuccess('Suppression reason deleted');
      fetchSuppressionReasons();
      return true;
    } catch (error) {
      console.error('Failed to delete suppression reason:', error);
      showError(error);
      return false;
    }
  };

  const handleReorderSuppressionReasons = async (orderedIds) => {
    try {
      await reorderSuppressionReasons(orderedIds);
      showSuccess('Suppression reasons reordered');
      fetchSuppressionReasons();
      return true;
    } catch (error) {
      console.error('Failed to reorder suppression reasons:', error);
      showError(error);
      return false;
    }
  };

  return (
    <TrustGridPage title="Detection Plan Configuration" breadcrumbs={breadcrumbs}>
      <Alert
        message="Admin Configuration"
        description="Configure findings lifecycle dispositions, wait-until timeframes, and suppression reasons for your organization."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane
            tab={
              <span>
                <ControlOutlined /> Dispositions
              </span>
            }
            key="dispositions"
          >
            <DispositionConfigPanel
              type="dispositions"
              inProgressData={dispositionsData.in_progress}
              closedData={dispositionsData.closed}
              onCreate={handleCreateDisposition}
              onUpdate={handleUpdateDisposition}
              onDelete={handleDeleteDisposition}
              onReorder={handleReorderDispositions}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ClockCircleOutlined /> Wait Until Timeframes
              </span>
            }
            key="timeframes"
          >
            <DispositionConfigPanel
              type="timeframes"
              data={timeframes}
              onCreate={handleCreateTimeframe}
              onUpdate={handleUpdateTimeframe}
              onDelete={handleDeleteTimeframe}
              onReorder={handleReorderTimeframes}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <StopOutlined /> Suppression Reasons
              </span>
            }
            key="suppression"
          >
            <DispositionConfigPanel
              type="suppression"
              data={suppressionReasons}
              onCreate={handleCreateSuppressionReason}
              onUpdate={handleUpdateSuppressionReason}
              onDelete={handleDeleteSuppressionReason}
              onReorder={handleReorderSuppressionReasons}
            />
          </TabPane>
        </Tabs>
      </Spin>
    </TrustGridPage>
  );
}

export default DetectionPlan;
